import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DataService } from 'src/app/data.service';
import { environment } from 'src/environments/environment';
import moment from 'moment';

interface TantargySor {
  tantargy: string;
  nev: string;
  rovid: string;
  sorrend: number | null;
}

interface EvfolyamForma {
  evfolyam: string;
  formak: string[];
}

interface TantervTetel {
  id: string;
  evfolyam: string;
  db: number;
}

interface TantervTantargy {
  tantargy: string;
  nev: string;
  rovid: string;
  szin: string;
  evfolyamok: TantervTetel[];
  _ujEvf?: string; // gyors évfolyam-hozzáadás beviteli mezője
  _ujDb?: number;
}

@Component({
  selector: 'app-admin-kerettanterv-sorrend',
  templateUrl: './kerettanterv-sorrend.component.html',
  styleUrls: ['./kerettanterv-sorrend.component.scss'],
})
export class AdminKerettantervSorrendComponent implements OnInit {
  sajatIntezmeny: any;
  aktTanevEv: string | null = null;
  tanevLabel = '';
  tantargyak: TantargySor[] = [];
  betoltesFolyamatban = false;
  mentesFolyamatban = false;

  readonly formaOpciok = [
    { key: 'szoveges', cimke: 'Szöveges' },
    { key: 'szazalek', cimke: 'Százalékos' },
    { key: 'jegy', cimke: 'Érdemjegyes' },
  ];

  // Értékelési formák modal állapota
  formaModalOpen = false;
  formaModalTantargy: TantargySor | null = null;
  formaEvfolyamok: EvfolyamForma[] = [];
  formaBetolt = false;
  formaMent = false;

  // Tanterv szerkesztő állapota
  tanterv: TantervTantargy[] = [];
  tantargyLista: any[] = []; // meglévő tantárgyak választóhoz
  tantervBetolt = false;
  ujHozzaadFolyamatban = false;
  uj = {
    ujTargy: false,
    tantargy: '' as string,
    nev: '',
    rovid: '',
    szin: '',
    evfolyam: '',
    db: 1,
  };

  constructor(private data: DataService, private http: HttpClient) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.aktTanevEv = await this.deriveTanevEv();
    if (this.aktTanevEv) {
      const ev = Number(this.aktTanevEv);
      this.tanevLabel = `${ev} / ${ev + 1}`;
    }
    await this.loadTantargyak();
    await this.loadTanterv();
    await this.loadTantargyLista();
  }

  private async deriveTanevEv(): Promise<string | null> {
    try {
      const akttev = JSON.parse(await this.data.getFromStorage('tanev')).aktualistanev;
      const tanevadatok = JSON.parse(await this.data.getFromStorage('tanevadatok'));
      const tanev = (Array.isArray(tanevadatok) ? tanevadatok : []).find((d: any) => d.id == akttev);
      if (tanev?.tankezd) return moment(tanev.tankezd).format('YYYY');
    } catch (e) {
      console.error('Tanév meghatározási hiba:', e);
    }
    return null;
  }

  private async loadTantargyak() {
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !this.aktTanevEv) return;
    this.betoltesFolyamatban = true;
    try {
      const params = new URLSearchParams({ ev: this.aktTanevEv });
      const rows = await this.http
        .get<TantargySor[]>(
          this.backendApiUrl('/api/kerettanterv-sorrend/' + intezmenyId) + '?' + params.toString()
        )
        .toPromise();
      this.tantargyak = Array.isArray(rows) ? rows : [];
    } catch (e) {
      console.error('Kerettanterv-sorrend betöltési hiba:', e);
      this.data.sendToast('Nem sikerült betölteni a tantárgyakat.', 'error');
    } finally {
      this.betoltesFolyamatban = false;
    }
  }

  drop(event: CdkDragDrop<TantargySor[]>) {
    moveItemInArray(this.tantargyak, event.previousIndex, event.currentIndex);
  }

  async mentes() {
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !this.aktTanevEv) {
      this.data.sendToast('Hiányzó intézmény vagy tanév.', 'error');
      return;
    }
    const items = this.tantargyak.map((t, i) => ({ tantargy: t.tantargy, sorrend: i }));
    this.mentesFolyamatban = true;
    try {
      await this.http
        .put(this.backendApiUrl('/api/kerettanterv-sorrend/' + intezmenyId), {
          ev: this.aktTanevEv,
          items,
        })
        .toPromise();
      this.data.sendToast('Tantárgysorrend mentve.', 'success');
      await this.loadTantargyak();
    } catch (e) {
      console.error('Kerettanterv-sorrend mentési hiba:', e);
      this.data.sendToast('Nem sikerült menteni a sorrendet.', 'error');
    } finally {
      this.mentesFolyamatban = false;
    }
  }

  // ===== Tanterv szerkesztő (tantárgy↔évfolyam hozzárendelések) =====

  private async loadTanterv() {
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !this.aktTanevEv) return;
    this.tantervBetolt = true;
    try {
      const params = new URLSearchParams({ ev: this.aktTanevEv });
      const rows = await this.http
        .get<any[]>(this.backendApiUrl('/api/kerettanterv/' + intezmenyId) + '?' + params.toString())
        .toPromise();
      const csoport = new Map<string, TantervTantargy>();
      for (const r of Array.isArray(rows) ? rows : []) {
        const key = String(r.tantargy);
        if (!csoport.has(key)) {
          csoport.set(key, {
            tantargy: key,
            nev: r.nev || '',
            rovid: r.rovid || '',
            szin: r.szin || '',
            evfolyamok: [],
          });
        }
        csoport.get(key)!.evfolyamok.push({ id: String(r.id), evfolyam: String(r.evfolyam), db: Number(r.db) });
      }
      this.tanterv = [...csoport.values()];
    } catch (e) {
      console.error('Tanterv betöltési hiba:', e);
      this.data.sendToast('Nem sikerült betölteni a tantervet.', 'error');
    } finally {
      this.tantervBetolt = false;
    }
  }

  private async loadTantargyLista() {
    if (!this.aktTanevEv) return;
    try {
      const lista = await this.data
        .getTantargy(this.aktTanevEv, this.sajatIntezmeny?.intezmenyId)
        .toPromise();
      this.tantargyLista = (Array.isArray(lista) ? lista : []).sort((a: any, b: any) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), 'hu')
      );
    } catch (e) {
      console.error('Tantárgylista betöltési hiba:', e);
    }
  }

  async ujHozzarendeles() {
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !this.aktTanevEv) return;
    const evfolyam = String(this.uj.evfolyam || '').trim();
    if (!evfolyam) {
      this.data.sendToast('Adj meg évfolyamot.', 'error');
      return;
    }
    const body: any = {
      ev: Number(this.aktTanevEv),
      evfolyam,
      db: Number(this.uj.db) > 0 ? Number(this.uj.db) : 1,
    };
    if (this.uj.ujTargy) {
      const nev = String(this.uj.nev || '').trim();
      if (!nev) {
        this.data.sendToast('Adj meg tantárgynevet.', 'error');
        return;
      }
      body.ujTantargy = { name: nev, rovid: this.uj.rovid, szin: this.uj.szin };
    } else {
      if (!this.uj.tantargy) {
        this.data.sendToast('Válassz tantárgyat.', 'error');
        return;
      }
      body.tantargy = this.uj.tantargy;
    }

    this.ujHozzaadFolyamatban = true;
    try {
      await this.http.post(this.backendApiUrl('/api/kerettanterv/' + intezmenyId), body).toPromise();
      this.data.sendToast('Hozzáadva.', 'success');
      this.uj.evfolyam = '';
      this.uj.nev = '';
      this.uj.rovid = '';
      this.uj.szin = '';
      await this.frissitMindent();
    } catch (e: any) {
      console.error('Hozzárendelés hiba:', e);
      const uzenet = e?.error?.error || 'Nem sikerült hozzáadni.';
      this.data.sendToast(uzenet, 'error');
    } finally {
      this.ujHozzaadFolyamatban = false;
    }
  }

  async gyorsEvfolyam(t: TantervTantargy, evfolyam: string, db: number) {
    const intezmenyId = this.intezmenyPath();
    const evf = String(evfolyam || '').trim();
    if (!intezmenyId || !this.aktTanevEv || !evf) {
      this.data.sendToast('Adj meg évfolyamot.', 'error');
      return;
    }
    try {
      await this.http
        .post(this.backendApiUrl('/api/kerettanterv/' + intezmenyId), {
          ev: Number(this.aktTanevEv),
          tantargy: t.tantargy,
          evfolyam: evf,
          db: Number(db) > 0 ? Number(db) : 1,
        })
        .toPromise();
      this.data.sendToast('Évfolyam hozzáadva.', 'success');
      await this.frissitMindent();
    } catch (e: any) {
      const uzenet = e?.error?.error || 'Nem sikerült hozzáadni.';
      this.data.sendToast(uzenet, 'error');
    }
  }

  async torolHozzarendeles(tetel: TantervTetel) {
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !tetel?.id) return;
    try {
      await this.http
        .delete(this.backendApiUrl('/api/kerettanterv/' + tetel.id + '/' + intezmenyId))
        .toPromise();
      this.data.sendToast('Hozzárendelés törölve.', 'success');
      await this.frissitMindent();
    } catch (e) {
      console.error('Törlési hiba:', e);
      this.data.sendToast('Nem sikerült törölni.', 'error');
    }
  }

  private async frissitMindent() {
    await this.loadTanterv();
    await this.loadTantargyak();
    await this.loadTantargyLista();
  }

  // ===== Értékelési formák modal (tantárgy + évfolyam szinten) =====

  async openFormaModal(t: TantargySor) {
    this.formaModalTantargy = t;
    this.formaModalOpen = true;
    this.formaEvfolyamok = [];
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !this.aktTanevEv) return;
    this.formaBetolt = true;
    try {
      const params = new URLSearchParams({ ev: this.aktTanevEv, tantargy: t.tantargy });
      const rows = await this.http
        .get<EvfolyamForma[]>(
          this.backendApiUrl('/api/ertekeles-forma/' + intezmenyId) + '?' + params.toString()
        )
        .toPromise();
      this.formaEvfolyamok = (Array.isArray(rows) ? rows : []).map((r: any) => ({
        evfolyam: String(r.evfolyam),
        formak: Array.isArray(r.formak) ? [...r.formak] : ['szoveges'],
      }));
    } catch (e) {
      console.error('Értékelési formák betöltési hiba:', e);
      this.data.sendToast('Nem sikerült betölteni az évfolyamokat.', 'error');
    } finally {
      this.formaBetolt = false;
    }
  }

  closeFormaModal() {
    this.formaModalOpen = false;
    this.formaModalTantargy = null;
    this.formaEvfolyamok = [];
  }

  isFormaKivalasztva(sor: EvfolyamForma, forma: string): boolean {
    return sor.formak.includes(forma);
  }

  toggleForma(sor: EvfolyamForma, forma: string, checked: boolean) {
    const benne = sor.formak.includes(forma);
    if (checked && !benne) sor.formak = [...sor.formak, forma];
    if (!checked && benne) sor.formak = sor.formak.filter((f) => f !== forma);
    // Legalább egy forma maradjon kiválasztva.
    if (!sor.formak.length) sor.formak = ['szoveges'];
  }

  async mentFormak() {
    const intezmenyId = this.intezmenyPath();
    if (!intezmenyId || !this.aktTanevEv || !this.formaModalTantargy) return;
    this.formaMent = true;
    try {
      await this.http
        .put(this.backendApiUrl('/api/ertekeles-forma/' + intezmenyId), {
          ev: this.aktTanevEv,
          tantargy: this.formaModalTantargy.tantargy,
          items: this.formaEvfolyamok.map((s) => ({ evfolyam: s.evfolyam, formak: s.formak })),
        })
        .toPromise();
      this.data.sendToast('Értékelési formák mentve.', 'success');
      this.closeFormaModal();
    } catch (e) {
      console.error('Értékelési formák mentési hiba:', e);
      this.data.sendToast('Nem sikerült menteni a formákat.', 'error');
    } finally {
      this.formaMent = false;
    }
  }

  private backendApiUrl(path: string): string {
    const apiOrigin = (environment.apiOrigin || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return apiOrigin ? `${apiOrigin}${normalizedPath}` : normalizedPath;
  }

  private intezmenyPath(): string {
    return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }
}
